/**
 * Stoneflow Pro Offline Module
 * 
 * Provides offline-first functionality for installers working in the field.
 * 
 * Features:
 * - React Query persistence to IndexedDB
 * - Action queue for offline mutations
 * - iOS-compatible sync fallback
 * - Image compression for uploads
 * - Conflict resolution
 */

// Existing hooks
export { useOnlineStatus } from '@/hooks/useOnlineStatus';
export { useNetworkQuality } from '@/hooks/useNetworkQuality';
export { usePwaUpdate } from '@/hooks/usePwaUpdate';

// React Query persister for IndexedDB
export { queryPersister } from './persister';

// Storage utilities
export { 
  getStorageEstimate, 
  checkStorageHealth, 
  clearAllOfflineData,
  getAllStorageKeys,
} from './persister';

// Image compression
export { compressImage } from './image-compressor';

// Action queue
export { 
  queueAction, 
  getPendingActions, 
  getPendingCount,
  getFailedActions,
  getFailedCount,
  getAllActions,
  removeAction,
  markActionFailed,
  retryAction,
  discardAction,
  clearActionQueue,
  formatActionDescription,
  MAX_QUEUE_SIZE,
  MAX_RETRIES,
  type PendingAction,
  type OfflineActionType,
  type ActionStatus,
} from './action-queue';

// Sync manager
export { syncManager, type SyncResult, type SyncEvent } from './sync-manager';

// Conflict resolver
export { 
  checkForConflict, 
  resolveConflict,
  formatConflictDescription,
  type ConflictInfo,
  type ConflictResolution,
} from './conflict-resolver';

/**
 * Query keys that should be persisted for offline access.
 * Only installer-critical data - admin data stays online-only.
 */
export const OFFLINE_QUERY_KEYS = [
  'my-loads',
  'my-load-installations', 
  'claimed-pickups',
] as const;

export type OfflineQueryKey = typeof OFFLINE_QUERY_KEYS[number];

/**
 * Check if a query key should be persisted for offline use.
 */
export function shouldPersistQuery(queryKey: unknown[]): boolean {
  const key = queryKey[0];
  if (typeof key !== 'string') return false;
  return OFFLINE_QUERY_KEYS.includes(key as OfflineQueryKey);
}
